// PRD 14 §3.3 / §3.4 — register-new + edit-draft form.
// Shared component used in both 'new' and 'edit' modes; chooses the mutation
// based on the store's mode and switches the version field between editable
// (new) and read-only (edit — version is immutable per contract §3.8).
//
// Slice 5 scope: Save Draft only. The [Publish] action lands in Slice 6.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';

import { isPermissionDenied } from '@/api/permission-error';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { toast } from '@/components/feedback/useToast';
import { hasEmoji, hasEmojiInRichText } from '@/shared/utils/emoji';
import { isRichTextTooLong } from '@/shared/utils/rich-text-length';

import {
  useCreateTermVersion,
  usePublishTermVersion,
  useUpdateTermVersion,
} from '../hooks/use-term-mutations';
import { useTermsDetailStore } from '../store/terms-detail.store';
import { type TermType } from '../types/terms.types';
import { getTermsErrorMessage, getTermsFieldErrors } from '../utils/error-message';
import { RteToolbar, type RteCommand } from './RteToolbar';
import { VersionMetaGrid } from './VersionMetaGrid';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

interface VersionFormProps {
  activeType: TermType;
}

// PRD 14 §4.2 — version must match v + digit.digit (e.g. "v1.5").
const VERSION_REGEX = /^v\d+\.\d+$/;

// Match the mock's "today" definition (UTC) so client-side validation
// gives the same verdict the server will. See contract §4 Q5.
function todayUtc(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Same body-empty check the mock uses (contract §2.5).
function hasContent(html: string): boolean {
  const stripped = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return stripped.length > 0;
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// QC validates against the real backend, whose stored-HTML sanitizer keeps the
// semantic <strong>/<em> but strips the presentational <b>/<i> that
// document.execCommand emits — so Bold/Italic vanished after save while
// Underline (<u>) survived. Normalize to the semantic tags before persisting so
// the formatting survives the round-trip. DOM-based (same approach as
// shared/utils/sanitize-html) so nested formatting is preserved.
function toSemanticHtml(html: string): string {
  const template = document.createElement('template');
  template.innerHTML = html;
  const swap = (from: string, to: string) => {
    template.content.querySelectorAll(from).forEach((el) => {
      const repl = document.createElement(to);
      for (const attr of Array.from(el.attributes)) {
        repl.setAttribute(attr.name, attr.value);
      }
      while (el.firstChild) repl.appendChild(el.firstChild);
      el.replaceWith(repl);
    });
  };
  swap('b', 'strong');
  swap('i', 'em');
  return template.innerHTML;
}

export function VersionForm({ activeType }: VersionFormProps) {
  const { t } = useTranslation('terms');

  const mode = useTermsDetailStore((s) => s.mode);
  const editingVersion = useTermsDetailStore((s) => s.editingVersion);
  const draft = useTermsDetailStore((s) => s.draft);
  const snapshot = useTermsDetailStore((s) => s.snapshot);
  const fieldErrors = useTermsDetailStore((s) => s.fieldErrors);
  const currentVersionId = useTermsDetailStore((s) => s.currentVersionId);
  const updateDraft = useTermsDetailStore((s) => s.updateDraft);
  const setFieldErrors = useTermsDetailStore((s) => s.setFieldErrors);
  const openList = useTermsDetailStore((s) => s.openList);
  const openDetail = useTermsDetailStore((s) => s.openDetail);

  // Cancel discards the draft and returns to where the form was opened from:
  // edit → back to the read-only detail of the same version (prototype parity);
  // new → back to the version list (there is no detail to return to).
  const handleCancel = () => {
    if (mode === 'edit' && currentVersionId !== null) {
      openDetail(currentVersionId);
    } else {
      openList();
    }
  };

  // Defense-in-depth: the form is only reachable via the WRITE-gated New /
  // Edit buttons, but a user can lose WRITE mid-session — an administrator
  // changes their Role and SHCT-629 re-syncs it on the next navigation — while
  // the form is still mounted. Disable submission so the BE never sees an
  // unauthorized write attempt from this page.
  const { canWrite, denyProps, tooltip } = useWriteGuard('PUBLICATION');

  const createMutation = useCreateTermVersion();
  const updateMutation = useUpdateTermVersion();
  const publishMutation = usePublishTermVersion();

  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  // SHCT-702 — PRD 14 §8.2 puts 등록 / 게시 / 삭제 failures in a 팝업. They were toasts: a passing,
  // self-dismissing strip for the one class of outcome the operator must not miss, since a failed
  // save and a successful one otherwise leave the same screen behind.
  const [systemError, setSystemError] = useState<string | null>(null);

  // SHCT-582 — PRD 21 §7.4 P-4: the server's RBAC refusal is 토스트(error), 화면 유지. PRD 14 §8.2's
  // 팝업 rule covers this screen's own failures (등록 실패 · 저장 실패 · 게시 실패) and keeps them.
  const reportSystemError = (e: unknown, message: string) => {
    if (isPermissionDenied(e)) toast.error(message);
    else setSystemError(message);
  };
  // Distinguishes a save-only flow from a publish chain so the button labels
  // can show "Saving..." vs "Publishing..." separately while the same create
  // or update mutation is in flight.
  const [isPublishing, setIsPublishing] = useState(false);
  const savingOnly =
    !isPublishing && (createMutation.isPending || updateMutation.isPending);
  const submitting =
    isPublishing ||
    createMutation.isPending ||
    updateMutation.isPending ||
    publishMutation.isPending;

  const editorRef = useRef<HTMLDivElement>(null);

  // Set the editor's initial HTML imperatively once on mount. React does NOT
  // re-control innerHTML across renders — keystrokes mutate the DOM directly
  // and are pulled back into the store via handleInput. Resetting innerHTML on
  // every re-render would clobber the user's cursor.
  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = draft?.content ?? '';
    // The mode/version target only changes via store transitions that unmount
    // and remount this form (openNew / openEdit / openList), so a single
    // mount-time hydration is correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!draft) return null;

  const isEdit = mode === 'edit';
  const title = isEdit
    ? t('form.editTitle', { name: activeType.name, version: draft.version })
    : t('form.newTitle', { name: activeType.name });

  // Cancel / Back discard the in-progress draft. In edit mode, return to the
  // read-only detail of the same version (the bug was always going to the list);
  // openDetail clears draft/snapshot, and the original values come from
  // react-query, untouched. In new mode there is no prior detail → go to the list.

  // Pull the live DOM back into the draft, normalizing execCommand's
  // presentational <b>/<i> to the semantic tags the backend sanitizer keeps.
  const syncDraftFromEditor = () => {
    if (!editorRef.current) return;
    updateDraft({ content: toSemanticHtml(editorRef.current.innerHTML) });
  };

  const handleEditorInput = () => {
    syncDraftFromEditor();
  };

  const handleCommand = (cmd: RteCommand) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    // document.execCommand is deprecated but still supported in every shipping
    // browser. PRD 14 §2.2 #8 mandates parity with the existing Notices /
    // Health-Whitepaper editors which use the same API.
    document.execCommand(cmd, false);
    syncDraftFromEditor();
  };

  // contenteditable collapses the newlines in pasted plain text (only manual
  // Enter survives). Intercept the paste and re-insert the text with a <br> per
  // line break so multi-line pastes keep their structure. The text is escaped
  // first so clipboard HTML can't be injected into the editor.
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    const html = escapeHtml(text).replace(/\r\n?|\n/g, '<br>');
    document.execCommand('insertHTML', false, html);
    syncDraftFromEditor();
  };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    // 버전 번호 is immutable once the version exists — the update DTO does not even
    // carry it — so its format check belongs to the 'new' path only.
    if (!isEdit) {
      const v = draft.version.trim();
      if (!v) errors.version = t('errors.VERSION_REQUIRED');
      else if (!VERSION_REGEX.test(v)) errors.version = t('errors.VERSION_FORMAT_INVALID');
    }
    // SHCT-709 — 시행일 is editable in both modes now, and the server applies the
    // same EFFECTIVE_DATE_REQUIRED / EFFECTIVE_DATE_PAST guards on PATCH as on
    // POST. Run the identical client check so an edit never round-trips just to
    // come back with a server error the form could have shown inline.
    //
    // In edit mode the check runs only when the user actually touched the date.
    // A DRAFT parked long enough for its 시행일 to fall into the past must stay
    // editable — otherwise a body typo becomes unfixable, which is the exact
    // dead end this story was raised to remove. An untouched date is left out of
    // the PATCH body entirely, so the server never re-validates it either.
    if (!isEdit || draft.effectiveDate !== snapshot?.effectiveDate) {
      if (!draft.effectiveDate) {
        errors.effectiveDate = t('errors.EFFECTIVE_DATE_REQUIRED');
      } else if (draft.effectiveDate <= todayUtc()) {
        errors.effectiveDate = t('errors.EFFECTIVE_DATE_PAST');
      }
    }
    // SHCT-859 — 개정 사유 is free text persisted with the version, so §4.2's
    // emoji ban covers it; it had no validation beyond the 200-char maxLength.
    if (hasEmoji(draft.revisionNote)) {
      errors.revisionNote = t('common:errors.emojiNotAllowed');
    }
    if (!hasContent(draft.content)) {
      errors.content = t('errors.CONTENT_REQUIRED');
    } else if (isRichTextTooLong(draft.content)) {
      // SHCT-703 — PRD 14 §4.2 has specified `1~20,000자(HTML 포함)` all along,
      // deferring to [공통] 00 §3, but nothing enforced the upper bound: a
      // 20,001-character body saved and came back truncated to 201. Checked
      // before the emoji scan so an over-long body reports its real problem
      // rather than whatever else the tail happens to contain.
      errors.content = t('errors.CONTENT_TOO_LONG');
    } else if (hasEmojiInRichText(draft.content)) {
      // §4.2 lists no RICHTEXT class — 리치텍스트 is exempt from the §3 LENGTH
      // table only, never from the character policy.
      errors.content = t('common:errors.emojiNotAllowed');
    }
    return errors;
  };

  // SHCT-709 — PATCH body for an edit. The server applies only the non-null
  // fields (`UpdateTermVersionRequest`), so an untouched 개정 사유 / 시행일 is
  // omitted rather than echoed back: that keeps the request honest about what
  // the operator changed and avoids re-triggering the server's date guard on a
  // value nobody edited.
  const buildUpdateInput = () => {
    const input: { revisionNote?: string; effectiveDate?: string; content?: string } = {
      content: draft.content,
    };
    if (draft.revisionNote !== snapshot?.revisionNote) input.revisionNote = draft.revisionNote;
    if (draft.effectiveDate !== snapshot?.effectiveDate) input.effectiveDate = draft.effectiveDate;
    return input;
  };

  const handleSaveDraft = async () => {
    const localErrors = validate();
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      return;
    }
    try {
      if (isEdit) {
        if (currentVersionId === null) return;
        await updateMutation.mutateAsync({
          typeId: activeType.id,
          versionId: currentVersionId,
          input: buildUpdateInput(),
        });
      } else {
        await createMutation.mutateAsync({
          typeId: activeType.id,
          input: {
            version: draft.version.trim(),
            revisionNote: draft.revisionNote,
            effectiveDate: draft.effectiveDate,
            content: draft.content,
          },
        });
      }
      toast.success(t('toasts.draftSaved'));
      // Edit mode → return to the read-only detail of the same version
      // (prototype parity; the saved content is re-fetched via the update
      // mutation's detail invalidation). New mode has no prior detail → list.
      if (isEdit && currentVersionId !== null) {
        openDetail(currentVersionId);
      } else {
        openList();
      }
    } catch (err) {
      // PRD 14 §8.1 first: a validation failure belongs under its own field, whether the server named
      // the field or only sent the code. Anything else is a §8.2 system error → 팝업, not a toast.
      const fields = getTermsFieldErrors(err, t);
      if (fields) {
        setFieldErrors(fields);
      } else {
        // §8.2 separates 새 버전 등록 실패 from a plain save failure; only the create path is the former.
        reportSystemError(
          err,
          getTermsErrorMessage(err, t, isEdit ? 'errors.saveFailed' : 'errors.createFailed'),
        );
      }
    }
  };

  const handlePublishClick = () => {
    // PRD 14 §3.3 step 4 — validate locally before showing the confirm modal so
    // the user doesn't pay a modal round-trip just to see an inline error.
    const localErrors = validate();
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      return;
    }
    setShowPublishConfirm(true);
  };

  const handlePublishConfirm = async () => {
    setShowPublishConfirm(false);
    setIsPublishing(true);
    try {
      // Contract §3.7 + §3.9: BE has no "create-and-publish" combo endpoint;
      // FE must (a) create-or-update the DRAFT, then (b) call publish on the
      // resulting id. If step (a) succeeds and step (b) fails the new DRAFT is
      // already saved — surface a distinct message so the user knows.
      let versionId = currentVersionId;
      let draftPersisted = isEdit;

      if (isEdit) {
        if (currentVersionId === null) return;
        if (useTermsDetailStore.getState().isDirty()) {
          await updateMutation.mutateAsync({
            typeId: activeType.id,
            versionId: currentVersionId,
            input: buildUpdateInput(),
          });
        }
      } else {
        const created = await createMutation.mutateAsync({
          typeId: activeType.id,
          input: {
            version: draft.version.trim(),
            revisionNote: draft.revisionNote,
            effectiveDate: draft.effectiveDate,
            content: draft.content,
          },
        });
        versionId = created.id;
        draftPersisted = true;
      }

      if (versionId === null) return;

      try {
        await publishMutation.mutateAsync({
          typeId: activeType.id,
          versionId,
        });
      } catch (publishErr) {
        // Distinct path for "DRAFT saved but publish failed" so the toast can
        // tell the user their work isn't lost.
        const fallback = draftPersisted
          ? 'errors.draftSavedPublishFailed'
          : 'errors.publishFailed';
        reportSystemError(publishErr, getTermsErrorMessage(publishErr, t, fallback));
        openList();
        return;
      }

      toast.success(t('toasts.published'));
      openList();
    } catch (err) {
      // The save step inside publish failed — same split as above: §8.1 inline, §8.2 팝업.
      const fields = getTermsFieldErrors(err, t);
      if (fields) {
        setFieldErrors(fields);
      } else {
        reportSystemError(
          err,
          getTermsErrorMessage(err, t, isEdit ? 'errors.publishFailed' : 'errors.createFailed'),
        );
      }
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 'var(--space-4)',
        }}
      >
        <button
          type="button"
          className="btn btn-sm btn-default btn-outline"
          onClick={handleCancel}
        >
          <ArrowLeft size={14} />
          {t('form.back')}
        </button>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{title}</h2>
      </div>

      {/* SHCT-709 — 개정 사유·시행일 are editable in BOTH modes. What still separates
          the two is the 버전 번호: free text when registering, immutable afterwards
          (the update DTO does not carry it), so edit mode keeps it inside the
          read-only meta grid alongside 상태·등록일시·작성자. */}
      {isEdit && editingVersion ? (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <VersionMetaGrid version={editingVersion} omit={['effectiveDate', 'revisionNote']} />
        </div>
      ) : null}

      {!isEdit ? (
        <div className="form-field">
          <label className="form-field__label">
            {t('form.fields.version.label')} <span className="required">*</span>
          </label>
          <input
            type="text"
            className={`form-field__input${fieldErrors.version ? ' form-field__input--error' : ''}`}
            style={{ width: 200 }}
            placeholder={t('form.fields.version.placeholder')}
            value={draft.version}
            onChange={(e) => updateDraft({ version: e.target.value })}
          />
          {fieldErrors.version ? (
            <p className="form-field__error">{fieldErrors.version}</p>
          ) : null}
        </div>
      ) : null}

      <div className="form-field">
        <label className="form-field__label">{t('form.fields.revisionNote.label')}</label>
        <input
          type="text"
          className={`form-field__input${
            fieldErrors.revisionNote ? ' form-field__input--error' : ''
          }`}
          maxLength={200}
          placeholder={t('form.fields.revisionNote.placeholder')}
          value={draft.revisionNote}
          onChange={(e) => updateDraft({ revisionNote: e.target.value })}
        />
        {fieldErrors.revisionNote ? (
          <p className="form-field__error">{fieldErrors.revisionNote}</p>
        ) : null}
      </div>

      <div className="form-field">
        <label className="form-field__label">
          {t('form.fields.effectiveDate.label')} <span className="required">*</span>
        </label>
        <input
          type="date"
          className={`form-field__input${
            fieldErrors.effectiveDate ? ' form-field__input--error' : ''
          }`}
          style={{ width: 200 }}
          // SHCT-620 — bound the native picker to a 4-digit year. Chrome
          // otherwise accepts years up to 275760. No `max={today}` here: a
          // 시행일 is legitimately in the future.
          min="1000-01-01"
          max="9999-12-31"
          value={draft.effectiveDate}
          onChange={(e) => updateDraft({ effectiveDate: e.target.value })}
        />
        {fieldErrors.effectiveDate ? (
          <p className="form-field__error">{fieldErrors.effectiveDate}</p>
        ) : null}
      </div>

      <div className="form-field">
        <label className="form-field__label">
          {t('form.fields.content.label')} <span className="required">*</span>
        </label>
        <RteToolbar onCommand={handleCommand} />
        <div
          ref={editorRef}
          className="mg-rte-content"
          contentEditable
          suppressContentEditableWarning
          onInput={handleEditorInput}
          onPaste={handlePaste}
          style={{
            minHeight: 360,
            border: '1px solid var(--border)',
            borderRadius: '0 0 var(--radius-sm) var(--radius-sm)',
            padding: '10px 12px',
            background: '#fff',
            fontSize: 14,
            lineHeight: 1.6,
            outline: 'none',
            whiteSpace: 'pre-wrap',
          }}
        />
        {fieldErrors.content ? (
          <p className="form-field__error">{fieldErrors.content}</p>
        ) : null}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 'var(--space-2)',
          marginTop: 'var(--space-4)',
        }}
      >
        <button
          type="button"
          className="btn btn-default btn-secondary"
          onClick={handleCancel}
          disabled={submitting}
        >
          {t('form.actions.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-default btn-secondary"
          onClick={handleSaveDraft}
          disabled={submitting || !canWrite}
          {...denyProps}
        >
          {savingOnly ? t('form.actions.saving') : t('form.actions.saveDraft')}
        </button>
        <button
          type="button"
          className="btn btn-default btn-primary"
          onClick={handlePublishClick}
          disabled={submitting || !canWrite}
          {...denyProps}
        >
          {isPublishing ? t('form.actions.publishing') : t('form.actions.publish')}
        </button>
      </div>

      <ConfirmModal
        open={showPublishConfirm}
        title={t('form.confirmPublish.title')}
        message={t('form.confirmPublish.message', {
          type: activeType.name,
          version: draft.version.trim() || draft.version,
        })}
        confirmLabel={t('form.confirmPublish.confirm')}
        cancelLabel={t('form.confirmPublish.cancel')}
        confirmVariant="primary"
        loading={isPublishing}
        onConfirm={handlePublishConfirm}
        onCancel={() => {
          if (isPublishing) return;
          setShowPublishConfirm(false);
        }}
      />
      <InfoModal
        open={systemError !== null}
        title={t('systemErrorModal.title')}
        body={systemError ?? ''}
        confirmLabel={t('systemErrorModal.confirm')}
        onClose={() => setSystemError(null)}
      />
    </div>
  );
}
